import { normalizeOrderCardListForDisplay } from '@/api/careOrderLineDisplay'
import type { CareOrderListItem } from '@/api/orders'
import { careOrderListItemToCardDto } from '@/api/orders'
import type { OrderCardDTO } from '@/api/orders'
import type { TechnicianListItem } from '@/api/technicianslist'

/**
 * 技术员列表卡片 Inspector 下绿色 ✓ 后数字 = 接口 `license_no`（TechniciansPage.vue → tech.licenseNo）。
 * 顶部 Technician ID 为 `technician_no`（如 I0000005），订单检查员旁展示须与 license_no 一致。
 */
export interface TechnicianNoLookup {
  byId: Map<string, string>
  byName: Map<string, string>
}

export function normalizeAssignmentKey(name: string): string {
  return name.trim().replace(/\s+/g, ' ').toLowerCase()
}

export function stripTechnicianNoPrefix(raw: string | undefined | null): string {
  return String(raw ?? '')
    .trim()
    .replace(/^@+\s*/, '')
}

export function technicianRefFromTechnicianNo(technicianNo: string | undefined | null): string | undefined {
  const n = stripTechnicianNoPrefix(technicianNo)
  if (!n) return undefined
  return `@ ${n}`
}

/** 按技术员 list 的 license_no 建索引（与页面绿色执照号一致） */
export function buildTechnicianNoLookup(list: TechnicianListItem[]): TechnicianNoLookup {
  const byId = new Map<string, string>()
  const byName = new Map<string, string>()
  for (const item of list) {
    const name = (item.name ?? '').trim()
    const licenseNo = (item.license_no ?? '').trim()
    if (!name || !licenseNo) continue
    byId.set(String(item.id), licenseNo)
    byName.set(normalizeAssignmentKey(name), licenseNo)
  }
  return { byId, byName }
}

export function technicianNoForAssignmentName(
  assignment: string | undefined,
  lookup: TechnicianNoLookup | null,
): string | undefined {
  const key = normalizeAssignmentKey(assignment ?? '')
  if (!key || /pending\s+assignment/i.test(key) || !lookup) return undefined
  return lookup.byName.get(key)
}

/** 执照号：先 list 的 license_no，再订单 technician 上的 license_no / badgeId / staff_no */
export function technicianNoFromApiTechnicianObject(
  tech: Record<string, unknown> | null | undefined,
  assignmentName: string | undefined,
  lookup: TechnicianNoLookup | null,
): string | undefined {
  const name = String(tech?.name ?? tech?.label ?? assignmentName ?? '').trim()
  if (name && lookup) {
    const fromList = technicianNoForAssignmentName(name, lookup)
    if (fromList) return stripTechnicianNoPrefix(fromList)
  }
  if (!tech) return undefined
  const raw =
    tech.license_no ??
    tech.licenseNo ??
    tech.badgeId ??
    tech.badge_id ??
    tech.staff_no ??
    tech.staffNo
  return stripTechnicianNoPrefix(raw != null ? String(raw) : undefined)
}

export function technicianRefFromApiTechnicianObject(
  tech: Record<string, unknown> | null | undefined,
  assignmentName: string | undefined,
  lookup: TechnicianNoLookup | null,
): string | undefined {
  return technicianRefFromTechnicianNo(
    technicianNoFromApiTechnicianObject(tech, assignmentName, lookup),
  )
}

function technicianObjectForLine(
  row: CareOrderListItem,
  lineIndex: number,
): Record<string, unknown> | null {
  if (lineIndex === 0) {
    const t = row.technician
    return t && typeof t === 'object' ? (t as Record<string, unknown>) : null
  }
  const fu = row.followups?.[lineIndex - 1]
  const t = fu?.technician
  return t && typeof t === 'object' ? (t as Record<string, unknown>) : null
}

export function patchOrderCardTechnicianRefs(
  card: OrderCardDTO,
  row: CareOrderListItem,
  lookup: TechnicianNoLookup | null,
): OrderCardDTO {
  let changed = false
  const services = card.services.map((line, lineIndex) => {
    const assignName = (line.assignment ?? '').trim()
    if (assignName && lookup && !/pending\s+assignment/i.test(assignName)) {
      const fromList = technicianNoForAssignmentName(assignName, lookup)
      const refFromList = technicianRefFromTechnicianNo(fromList)
      if (refFromList && refFromList !== line.technicianRef) {
        changed = true
        return { ...line, technicianRef: refFromList }
      }
    }
    const tech = technicianObjectForLine(row, lineIndex)
    const ref = technicianRefFromApiTechnicianObject(tech, line.assignment, lookup)
    if (!ref || ref === line.technicianRef) return line
    changed = true
    return { ...line, technicianRef: ref }
  })
  return changed ? { ...card, services } : card
}

export function mapCareOrderListToCardsRaw(
  listRaw: unknown[],
  lookup: TechnicianNoLookup | null,
): OrderCardDTO[] {
  return listRaw.map((x) => {
    const row = x as CareOrderListItem
    return patchOrderCardTechnicianRefs(careOrderListItemToCardDto(row), row, lookup)
  })
}

export function mapCareOrderListToCards(
  listRaw: unknown[],
  lookup: TechnicianNoLookup | null,
): OrderCardDTO[] {
  return normalizeOrderCardListForDisplay(mapCareOrderListToCardsRaw(listRaw, lookup))
}

export const TECHNICIAN_NO_DISPLAY_MAX_LEN = 20

export function truncateTechnicianNoDisplay(
  full: string,
  maxLen = TECHNICIAN_NO_DISPLAY_MAX_LEN,
): string {
  const text = (full ?? '').trim()
  if (text.length <= maxLen) return text
  return `${text.slice(0, maxLen)}..`
}

export function isTechnicianNoTruncated(
  full: string,
  maxLen = TECHNICIAN_NO_DISPLAY_MAX_LEN,
): boolean {
  return (full ?? '').trim().length > maxLen
}

/** 订单 Staff ID 展示：与技术员页绿色执照号（license_no）一致 */
export function displayTechnicianNo(
  assignment: string | undefined,
  technicianRef: string | number | undefined | null,
  lookup: TechnicianNoLookup | null,
): string {
  const fromList = technicianNoForAssignmentName(assignment, lookup)
  if (fromList) return stripTechnicianNoPrefix(fromList)
  return stripTechnicianNoPrefix(technicianRef != null ? String(technicianRef) : undefined)
}

export function applyTechnicianRefsToOrderCard(
  order: OrderCardDTO,
  lookup: TechnicianNoLookup | null,
): OrderCardDTO {
  if (!lookup) return order
  let changed = false
  const services = order.services.map((line) => {
    const name = (line.assignment ?? '').trim()
    if (!name || /pending\s+assignment/i.test(name)) return line
    const ref = technicianRefFromTechnicianNo(technicianNoForAssignmentName(name, lookup))
    if (!ref || ref === line.technicianRef) return line
    changed = true
    return { ...line, technicianRef: ref }
  })
  return changed ? { ...order, services } : order
}
